import { config, isValidQrCodeId } from '../config.js';

export class QrError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'QrError';
    this.code = code;
  }
}

/** Failures that mean "there is nothing here" and so render the 404 page. */
export const NOT_FOUND_CODES = new Set([
  'QR_CODE_NOT_FOUND',
  'INVALID_QR_CODE_ID',
  'BAD_TARGET',
]);

export const isNotFound = (code) => NOT_FOUND_CODES.has(code);

/** Never navigate to anything but http(s), whatever the API returns. */
function safeUrl(raw) {
  let url;
  try {
    url = new URL(raw);
  } catch {
    throw new QrError('BAD_TARGET', 'The QR code points at a malformed URL');
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new QrError('BAD_TARGET', `Refusing to open a ${url.protocol} URL`);
  }
  return url.toString();
}

const parseJson = (text) => {
  try {
    const body = JSON.parse(text);
    return body && typeof body === 'object' ? body : null;
  } catch {
    return null;
  }
};

/**
 * Pull the target out of the response envelope:
 *
 *   { "data": "https://…", "success": true, "message": "Asset url" }
 *
 * `data` is a plain URL string. The other shapes are accepted too so the app
 * survives the envelope changing again — it has already gone from a bare 302
 * to this once.
 */
export function urlFromBody(body) {
  if (!body) return null;

  return (
    [
      body.data,
      body.data?.url,
      body.data?.redirectUrl,
      body.data?.link,
      body.url,
      body.redirectUrl,
      body.redirectURL,
      body.targetUrl,
      body.link,
      body.location,
    ].find((value) => typeof value === 'string' && value.trim()) ?? null
  );
}

const cancelBody = (res) => {
  try {
    res.body?.cancel?.();
  } catch {
    /* nothing to cancel */
  }
};

/**
 * Ask the DAM which URL a QR code points at, for this particular device.
 *
 * The API answers 200 with the target in `data`. An unknown code is *also* a
 * 200 — with `data: ""` and "QR code information not available" — so an empty
 * target, not the HTTP status, is what marks a dead end here.
 *
 * The redirect branch below handles the older behaviour, where the answer was
 * a 302 whose Location header held the target. A browser cannot read that
 * header (redirect:'manual' yields an opaque response), so the redirect is
 * followed and the target read back off response.url. Keeping it costs
 * nothing and means a rollback on the API side does not take the app down.
 *
 * The API rejects a missing or empty deviceId / deviceName with a 401, which
 * is why the caller must always supply both.
 */
export async function resolveQrTarget(
  { qrCodeId, deviceId, deviceName, domain },
  { fetchImpl = fetch, apiUrl = config.apiUrl, timeoutMs = config.timeoutMs } = {},
) {
  if (!isValidQrCodeId(qrCodeId)) {
    throw new QrError('INVALID_QR_CODE_ID', 'That is not an Ecollat QR code');
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetchImpl(apiUrl, {
      method: 'POST',
      redirect: 'follow',
      cache: 'no-store',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        qrCodeId,
        deviceId: deviceId || 'unknown',
        deviceName: deviceName || 'Unknown Device',
        domain,
      }),
      signal: controller.signal,
    });
  } catch (err) {
    throw err?.name === 'AbortError'
      ? new QrError('TIMEOUT', 'The QR service did not respond in time')
      : new QrError('NETWORK_ERROR', 'Could not reach the QR service');
  } finally {
    clearTimeout(timer);
  }

  // Legacy 302 path: the browser followed the redirect and res.url is where it
  // landed. Anything still pointing at the API means no redirect happened.
  const landedElsewhere = res.url && res.url.split('?')[0] !== apiUrl.split('?')[0];
  if (res.redirected && landedElsewhere) {
    cancelBody(res);
    return safeUrl(res.url);
  }

  if (res.status === 404) {
    cancelBody(res);
    throw new QrError('QR_CODE_NOT_FOUND', 'This QR code is not active');
  }

  if (!res.ok) {
    cancelBody(res);
    // A missing or empty device field lands here as "Unauthorized: Missing token".
    throw new QrError(
      res.status === 401 || res.status === 400 ? 'INVALID_REQUEST' : `HTTP_${res.status}`,
      `The QR service responded with ${res.status}`,
    );
  }

  const body = parseJson(await res.text());

  // An explicit failure flag is a fault on their side, not a missing code.
  if (body?.success === false) {
    throw new QrError('UPSTREAM_ERROR', body.message || 'The QR service reported a failure');
  }

  const url = urlFromBody(body);

  // No target in a successful response is how this API says "no such code":
  // data is "" and message reads "QR code information not available".
  if (!url) {
    throw new QrError('QR_CODE_NOT_FOUND', body?.message || 'This QR code is not active');
  }

  return safeUrl(url);
}
